import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type {
  CommunityMemberBillingInfo,
  Paywall,
  PaywallCheckoutPreview,
} from "@/react/types";
import { getCurrentCircleLocale } from "@circle-react/helpers/localeHelpers";
import { isPix } from "@circle-react/helpers/paywallCheckoutHelpers";

export const GENERAL_ERROR = {
  message: t("services.paywalls.failures.internal.generic"),
};

export const STRIPE_CONNECTION_ERROR = {
  message: t("paywall_checkout.form.errors.stripe_connection_failed"),
};

export const PIX_PAYMENT_EXPIRED = {
  message: t("paywall_checkout.form.errors.pix_payment.time_expired"),
  error_details: "PIX_PAYMENT_EXPIRED",
};

export const PIX_MODAL_CLOSED = {
  message: t("paywall_checkout.form.errors.pix_payment.pix_modal_closed"),
  error_details: "PIX_MODAL_CLOSED",
};

export const getReferral = () => window.Rewardful?.referral;

export const DEFAULT_ERROR = {
  disablePayButton: true,
  type: "danger",
  existingAccount: false,
  hasAlreadyPurchased: false,
  message: null,
};

interface BuildErrorMessageOptions {
  loginUrl?: string;
  resetPasswordRedirectUrl?: string;
}

export const buildErrorMessage = (
  error?: any,
  { loginUrl, resetPasswordRedirectUrl }: BuildErrorMessageOptions = {},
) => {
  const errors: Record<string, any> = {
    USER_LOGIN_REQUIRED: {
      disablePayButton: false,
      type: "warning",
      existingAccount: true,
      message: t("paywall_checkout.form.errors.existing_account", {
        sign_in_url: loginUrl,
        reset_password_url: resetPasswordRedirectUrl,
      }),
    },
    INVALID_COUPON: { message: error?.message || "" },
    OTP_SESSION_EXPIRED: {
      disablePayButton: false,
      message: error?.message || "",
    },
    PIX_PAYMENT_EXPIRED,
    PIX_MODAL_CLOSED,
    PROCESSOR_ERROR: {
      disablePayButton: false,
      message: error?.message || "",
      type: "danger",
    },
  };

  return error ? errors[error.error_details] : GENERAL_ERROR;
};

interface BuildPayButtonLabel {
  currency: Paywall["currency"];
  isPaywallActive: boolean;
  preview?: PaywallCheckoutPreview;
  selectedPrice: any;
}

export const buildPayButtonLabel = ({
  isPaywallActive,
  selectedPrice,
  preview,
  currency,
}: BuildPayButtonLabel) => {
  if (!isPaywallActive || !selectedPrice) {
    return t("paywall_checkout.form.buttons.draft_pay");
  }

  if (selectedPrice?.trial_enabled) {
    if (selectedPrice?.trial_requires_upfront_payment) {
      return t("paywall_checkout.form.buttons.paid_trial");
    }
    return t("paywall_checkout.form.buttons.trial");
  }
  if (
    preview &&
    preview?.amount_due_now === 0 &&
    selectedPrice?.display_interval
  ) {
    return t("paywall_checkout.form.buttons.subscribe");
  }
  if (
    preview &&
    preview?.amount_due_now === 0 &&
    !selectedPrice?.display_interval
  ) {
    return t("paywall_checkout.form.buttons.continue");
  }
  if (preview && preview.amount_due_now > 0) {
    return `${t("paywall_checkout.form.buttons.pay")} ${formatPaywallCurrencyAmount(
      currency,
      preview.amount_due_now,
    )}`;
  }

  return `${t("paywall_checkout.form.buttons.pay")} ${formatPaywallCurrencyAmount(
    currency,
    selectedPrice.amount,
  )}`;
};

export const PERMITTED_QUERY_PARAMETERS = [
  "price_id",
  "coupon_code",
  "affiliate_code",
  "event_id",
];

export const getValidQueryParameters = () => {
  const queryParameters = new URLSearchParams(window.location.search);

  queryParameters.forEach((_value, key) => {
    if (!PERMITTED_QUERY_PARAMETERS.includes(key)) {
      queryParameters.delete(key);
    }
  });

  return queryParameters;
};

export interface GetPreviewParametersReturn {
  locale: string;
  queryParams: {
    community_id: number;
    community_member_billing_info_attributes?:
      | CommunityMemberBillingInfo
      | NonNullable<unknown>;
    coupon_code: string;
    coupon_code_applied: boolean;
    payment_method_type: string;
    paywall_price_id: number;
  };
  shouldIgnoreCommunityMember: boolean;
}

export const getPreviewParameters = (
  params: GetPreviewParametersReturn["queryParams"],
): GetPreviewParametersReturn => {
  const {
    community_id,
    paywall_price_id,
    community_member_billing_info_attributes,
    coupon_code,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- We're using in multiple places this variable
    coupon_code_applied,
    payment_method_type,
  } = params;

  return {
    queryParams: {
      payment_method_type,
      community_id,
      paywall_price_id,
      community_member_billing_info_attributes,
      coupon_code,
      coupon_code_applied,
    },
    shouldIgnoreCommunityMember: isPix(payment_method_type),
    locale: getCurrentCircleLocale(),
  };
};

export const constructReturnUrlParams = (
  paymentMethodType: string,
  paymentIntentId?: string,
) => {
  const params = new URLSearchParams(window.location.search);
  params.set("payment_method_type", paymentMethodType);
  if (paymentIntentId) {
    params.set("payment_intent", paymentIntentId);
  }
  return params;
};

export const constructReturnUrlObject = (
  paymentMethodType: string,
  paymentIntentId?: string,
) => {
  const urlParams = constructReturnUrlParams(
    paymentMethodType,
    paymentIntentId,
  );
  const url = new URL(window.location.href);
  // Normalize the pathname to handle cases where users access URLs with double slashes
  url.pathname = url.pathname.replace(/^\/+/, "/");
  url.search = urlParams.toString();
  return url;
};

export const constructReturnUrl = (
  paymentMethodType: string,
  paymentIntentId?: string,
) => {
  const url = constructReturnUrlObject(paymentMethodType, paymentIntentId);
  return url.toString();
};

export const constructReturnUrlPath = (
  paymentMethodType: string,
  paymentIntentId?: string,
) => {
  const url = constructReturnUrlObject(paymentMethodType, paymentIntentId);
  return `${url.pathname}${url.search}`;
};
